import React from "react";
import {
  Box,
  Typography,
  Container,
  Toolbar,
  Chip,
  Stack,
  Divider,
  Avatar,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CardMedia,
  Paper,
  Breadcrumbs,
  Link as MuiLink,
  Button,
  IconButton,
  Skeleton,
  Fade,
  useMediaQuery,
} from "@mui/material";
import {
  Share as ShareIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Link as LinkIcon,
  ArrowBack as ArrowBackIcon,
  AccessTime as TimeIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { Helmet } from "react-helmet";
import HomeHeader from "../../../Components/HomeHeader";
import HomeFooter from "../../../Components/HomeFooter";
import API from "../../../API";
import { showSuccessToast } from "../../../Utils/toastNotifications";
import { useTheme } from '@mui/material/styles';


const ArticleDetailSkeleton = () => (
  <Box sx={{ animation: "pulse 1.5s ease-in-out infinite" }}>
    <Skeleton variant="text" height={60} width="90%" sx={{ mb: 2 }} />
    <Skeleton variant="text" height={30} width="50%" sx={{ mb: 3 }} />
    <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
      <Skeleton variant="circular" width={40} height={40} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" height={24} width="30%" />
        <Skeleton variant="text" height={20} width="40%" />
      </Box>
    </Box>
    <Skeleton variant="rectangular" height={450} sx={{ borderRadius: 3, mb: 4 }} />
    <Skeleton variant="text" height={30} sx={{ mb: 2 }} />
    <Skeleton variant="text" height={24} />
    <Skeleton variant="text" height={24} />
    <Skeleton variant="text" height={24} width="70%" />
  </Box>
);

const RelatedArticleSkeleton = () => (
  <Card sx={{ borderRadius: 2 }}>
    <Skeleton variant="rectangular" height={140} />
    <CardContent>
      <Skeleton variant="text" height={24} width="90%" />
      <Skeleton variant="text" height={20} width="50%" sx={{ mt: 1 }} />
    </CardContent>
  </Card>
);

const ArticleDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const { data: categoriesData } = useQuery(
    "articleCategories",
    () => API.articles.categories(),
    { staleTime: 300000 }
  );
  const categories = categoriesData?.data?.categories || [];

  const getCategorySlug = React.useCallback(
    (nameOrSlug) => {
      if (!nameOrSlug) return "";
      const match = categories.find(
        (c) =>
          c.name?.toLowerCase() === String(nameOrSlug).toLowerCase() ||
          c.slug === nameOrSlug
      );
      return match?.slug || String(nameOrSlug);
    },
    [categories]
  );

  const {
    data: articleData,
    isLoading,
    error,
  } = useQuery(["article", slug], () => API.articles.getBySlug(slug), {
    retry: 1,
    staleTime: 60000,
  });

  const article = articleData?.data?.article;
  const relatedArticles = articleData?.data?.related_articles || [];

  const handleShare = (platform) => {
    if (!article) return;
    const url = window.location.href;
    const text = article.title;

    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        url
      )}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        url
      )}`,
    };

    if (platform === "copy") {
      navigator.clipboard.writeText(url);
      showSuccessToast("Link copied to clipboard!");
    } else if (shareUrls[platform]) {
      window.open(shareUrls[platform], "_blank", "noopener,noreferrer,width=600,height=600");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (error) {
    return (
      <Box sx={{ backgroundColor: "#fafbfc", minHeight: "100vh" }}>
        <HomeHeader />
        <Toolbar />
        <Container maxWidth="md" sx={{ py: 8, textAlign: "center" }}>
          <Paper 
            sx={{ 
              p: 6, 
              borderRadius: 3,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: "#1a1a1a" }}>
              Article Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              The article you're looking for doesn't exist or has been removed.
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<ArrowBackIcon />} 
              onClick={() => navigate("/articles")}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
              }}
            >
              Back to Articles
            </Button>
          </Paper>
        </Container>
        <HomeFooter />
      </Box>
    );
  }

  const metaKeywords =
    article?.meta_keywords && Array.isArray(article.meta_keywords)
      ? article.meta_keywords.join(", ")
      : article?.meta_keywords || "";

  const schemaData = article?.schema_data;

  return (
    <Box sx={{ backgroundColor: "#fafbfc", minHeight: "100vh" }}>
      {article && (
        <Helmet>
          <title>{article.meta_title || article.title}</title>
          <meta name="description" content={article.meta_description || article.excerpt || ""} />
          {metaKeywords && <meta name="keywords" content={metaKeywords} />}
          {article.canonical_url && <link rel="canonical" href={article.canonical_url} />}
          <meta property="og:type" content={article.og_type || "article"} />
          <meta property="og:title" content={article.og_title || article.title} />
          <meta property="og:description" content={article.og_description || article.excerpt || ""} />
          <meta property="og:image" content={article.og_image || article.featured_image || ""} />
          <meta property="og:url" content={typeof window !== "undefined" ? window.location.href : ""} />
          <meta name="twitter:card" content={article.twitter_card_type || "summary_large_image"} />
          <meta name="twitter:title" content={article.twitter_title || article.title} />
          <meta name="twitter:description" content={article.twitter_description || article.excerpt || ""} />
          <meta name="twitter:image" content={article.twitter_image || article.featured_image || ""} />
          {schemaData && (
            <script type="application/ld+json">
              {JSON.stringify(schemaData)}
            </script>
          )}
          {(article.noindex || article.nofollow) && (
            <meta
              name="robots"
              content={`${article.noindex ? "noindex" : "index"},${article.nofollow ? "nofollow" : "follow"}`}
            />
          )}
        </Helmet>
      )}

      <HomeHeader />
      <Toolbar />

      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, sm: 3 } }}>
        {/* Back Button */}
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate("/articles")} 
          sx={{ 
            mb: 3, 
            textTransform: "none",
            fontWeight: 600,
            color: "#667eea",
            "&:hover": {
              backgroundColor: "rgba(102, 126, 234, 0.08)",
            },
          }}
        >
          Back to Articles
        </Button>

        <Grid container spacing={{ xs: 3, md: 4 }}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Fade in timeout={600}>
              <Paper 
                sx={{ 
                  p: { xs: 3, sm: 4, md: 6 }, 
                  borderRadius: 3,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                  background: "white",
                }}
              >
                {isLoading ? (
                  <ArticleDetailSkeleton />
                ) : article ? (
                  <>
                    {/* Breadcrumbs */}
                    <Breadcrumbs 
                      separator="›" 
                      sx={{ 
                        mb: 3,
                        "& .MuiBreadcrumbs-separator": {
                          color: "#999",
                        },
                      }}
                    >
                      <MuiLink
                        component="button"
                        onClick={() => navigate("/articles")}
                        sx={{
                          color: "#667eea",
                          textDecoration: "none",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        Articles
                      </MuiLink>
                      {article.category && (
                        <MuiLink
                          component="button"
                          onClick={() =>
                            navigate(
                              `/articles?category=${encodeURIComponent(
                                getCategorySlug(article.category)
                              )}`
                            )
                          }
                          sx={{
                            color: "#667eea",
                            textDecoration: "none",
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {article.category}
                        </MuiLink>
                      )}
                      <Typography sx={{ color: "#999", fontSize: "0.875rem", fontWeight: 500 }}>
                        {article.title}
                      </Typography>
                    </Breadcrumbs>

                    {/* Featured Badge */}
                    {article.is_featured && (
                      <Chip 
                        label="Featured Article" 
                        sx={{ 
                          mb: 3,
                          bgcolor: "rgba(102, 126, 234, 0.12)",
                          color: "#667eea",
                          fontWeight: 700,
                          fontSize: "0.875rem",
                          height: 32,
                        }} 
                      />
                    )}

                    {/* Title */}
                    <Typography 
                      variant={isMobile ? "h4" : "h3"} 
                      component="h1" 
                      sx={{ 
                        fontWeight: 800, 
                        mb: 2,
                        color: "#1a1a1a",
                        lineHeight: 1.2,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {article.title}
                    </Typography>

                    {/* Subtitle */}
                    {article.subtitle && (
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: "#666", 
                          mb: 4,
                          fontWeight: 400,
                          lineHeight: 1.5,
                        }}
                      >
                        {article.subtitle}
                      </Typography>
                    )}

                    {/* Meta Information */}
                    <Stack 
                      direction={isMobile ? "column" : "row"} 
                      spacing={2} 
                      alignItems={isMobile ? "flex-start" : "center"} 
                      flexWrap="wrap" 
                      sx={{ mb: 4, gap: 2 }}
                    >
                      {article.author && (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar 
                            sx={{ 
                              width: 44, 
                              height: 44,
                              bgcolor: "#667eea",
                              fontWeight: 700,
                            }}
                          >
                            {article.author.name?.[0] || "A"}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: "#1a1a1a" }}>
                              {article.author.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#999" }}>
                              {formatDate(article.published_at)}
                            </Typography>
                          </Box>
                        </Stack>
                      )}
                      {!!article.read_time_minutes && (
                        <Chip 
                          icon={<TimeIcon sx={{ fontSize: 18 }} />} 
                          label={`${article.read_time_minutes} min read`} 
                          size="medium"
                          sx={{
                            bgcolor: "rgba(0,0,0,0.05)",
                            fontWeight: 600,
                            border: "none",
                          }}
                        />
                      )}
                      {!!article.view_count && article.view_count > 0 && (
                        <Chip 
                          icon={<ViewIcon sx={{ fontSize: 18 }} />} 
                          label={`${article.view_count.toLocaleString()} views`} 
                          size="medium"
                          sx={{
                            bgcolor: "rgba(0,0,0,0.05)",
                            fontWeight: 600,
                            border: "none",
                          }}
                        />
                      )}
                    </Stack>

                    {/* Tags */}
                    {Array.isArray(article.tags) && article.tags.length > 0 && (
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 4, gap: 1 }}>
                        {article.tags.map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            onClick={() => navigate(`/articles?tag=${encodeURIComponent(tag)}`)}
                            sx={{ 
                              cursor: "pointer",
                              bgcolor: "rgba(102, 126, 234, 0.08)",
                              color: "#667eea",
                              fontWeight: 600,
                              transition: "all 0.2s",
                              "&:hover": {
                                bgcolor: "rgba(102, 126, 234, 0.15)",
                                transform: "translateY(-2px)",
                              },
                            }}
                          />
                        ))}
                      </Stack>
                    )}

                    <Divider sx={{ mb: 4 }} />

                    {/* Featured Image */}
                    {article.featured_image && (
                      <Box
                        component="img"
                        src={article.featured_image}
                        alt={article.featured_image_alt || article.title}
                        sx={{ 
                          width: "100%", 
                          maxHeight: 550, 
                          objectFit: "cover", 
                          borderRadius: 3, 
                          mb: 5,
                          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                        }}
                      />
                    )}

                    {/* Article Content */}
                    {article.content && (
                      <Box
                        sx={{
                          "& p": { 
                            mb: 3, 
                            lineHeight: 1.8, 
                            fontSize: { xs: "1.05rem", sm: "1.125rem" },
                            color: "#333",
                          },
                          "& h2": { 
                            mt: 5, 
                            mb: 3, 
                            fontWeight: 700,
                            fontSize: { xs: "1.75rem", sm: "2rem" },
                            color: "#1a1a1a",
                            letterSpacing: "-0.01em",
                          },
                          "& h3": { 
                            mt: 4, 
                            mb: 2.5, 
                            fontWeight: 700,
                            fontSize: { xs: "1.5rem", sm: "1.625rem" },
                            color: "#1a1a1a",
                          },
                          "& ul, & ol": { 
                            mb: 3, 
                            pl: { xs: 3, sm: 4 },
                            lineHeight: 1.8,
                          },
                          "& li": { 
                            mb: 1.5,
                            color: "#333",
                            fontSize: { xs: "1.05rem", sm: "1.125rem" },
                          },
                          "& img": { 
                            maxWidth: "100%", 
                            borderRadius: 3, 
                            my: 4,
                            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                          },
                          "& a": { 
                            color: "#667eea", 
                            textDecoration: "none",
                            fontWeight: 600,
                            borderBottom: "2px solid rgba(102, 126, 234, 0.3)",
                            transition: "all 0.2s",
                            "&:hover": {
                              borderBottomColor: "#667eea",
                            },
                          },
                          "& blockquote": {
                            borderLeft: "4px solid #667eea",
                            pl: 3,
                            py: 1,
                            my: 4,
                            fontStyle: "italic",
                            color: "#666",
                            bgcolor: "rgba(102, 126, 234, 0.05)",
                            borderRadius: "0 8px 8px 0",
                          },
                          "& code": {
                            bgcolor: "rgba(0,0,0,0.05)",
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: "0.9em",
                            fontFamily: "monospace",
                          },
                          "& pre": {
                            bgcolor: "#1a1a1a",
                            color: "#f5f5f5",
                            p: 3,
                            borderRadius: 2,
                            overflow: "auto",
                            my: 3,
                          },
                        }}
                        dangerouslySetInnerHTML={{ __html: article.content }}
                      />
                    )}

                    <Divider sx={{ my: 5 }} />

                    {/* Enhanced Share Section */}
                    <Box>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mb: 3, 
                          fontWeight: 700,
                          color: "#1a1a1a",
                        }}
                      >
                        Share this article
                      </Typography>
                      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
                        <IconButton
                          aria-label="Share on Twitter"
                          onClick={() => handleShare("twitter")}
                          sx={{ 
                            bgcolor: "#1DA1F2", 
                            color: "white", 
                            width: 48,
                            height: 48,
                            transition: "all 0.2s",
                            "&:hover": { 
                              bgcolor: "#1a8cd8",
                              transform: "translateY(-3px)",
                              boxShadow: "0 6px 16px rgba(29, 161, 242, 0.4)",
                            },
                          }}
                        >
                          <TwitterIcon />
                        </IconButton>
                        <IconButton
                          aria-label="Share on Facebook"
                          onClick={() => handleShare("facebook")}
                          sx={{ 
                            bgcolor: "#4267B2", 
                            color: "white",
                            width: 48,
                            height: 48,
                            transition: "all 0.2s",
                            "&:hover": { 
                              bgcolor: "#365899",
                              transform: "translateY(-3px)",
                              boxShadow: "0 6px 16px rgba(66, 103, 178, 0.4)",
                            },
                          }}
                        >
                          <FacebookIcon />
                        </IconButton>
                        <IconButton
                          aria-label="Share on LinkedIn"
                          onClick={() => handleShare("linkedin")}
                          sx={{ 
                            bgcolor: "#0077B5", 
                            color: "white",
                            width: 48,
                            height: 48,
                            transition: "all 0.2s",
                            "&:hover": { 
                              bgcolor: "#006399",
                              transform: "translateY(-3px)",
                              boxShadow: "0 6px 16px rgba(0, 119, 181, 0.4)",
                            },
                          }}
                        >
                          <LinkedInIcon />
                        </IconButton>
                        <IconButton
                          aria-label="Copy link"
                          onClick={() => handleShare("copy")}
                          sx={{ 
                            bgcolor: "#6c757d", 
                            color: "white",
                            width: 48,
                            height: 48,
                            transition: "all 0.2s",
                            "&:hover": { 
                              bgcolor: "#5a6268",
                              transform: "translateY(-3px)",
                              boxShadow: "0 6px 16px rgba(108, 117, 125, 0.4)",
                            },
                          }}
                        >
                          <LinkIcon />
                        </IconButton>
                      </Stack>
                    </Box>
                  </>
                ) : null}
              </Paper>
            </Fade>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: "sticky", top: 88 }}>
              {/* Related Articles */}
              {isLoading ? (
                <Paper sx={{ p: 3, borderRadius: 3, mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                  <Skeleton variant="text" height={32} width="70%" sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    {[1, 2, 3].map((i) => (
                      <RelatedArticleSkeleton key={i} />
                    ))}
                  </Stack>
                </Paper>
              ) : relatedArticles.length > 0 ? (
                <Fade in timeout={800}>
                  <Paper 
                    sx={{ 
                      p: 3, 
                      borderRadius: 3, 
                      mb: 3,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      background: "white",
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        mb: 3,
                        color: "#1a1a1a",
                      }}
                    >
                      Related Articles
                    </Typography>
                    <Stack spacing={2}>
                      {relatedArticles.map((relArticle) => (
                        <Card
                          key={relArticle.id}
                          sx={{
                            cursor: "pointer",
                            borderRadius: 2,
                            transition: "all 0.2s",
                            border: "1px solid",
                            borderColor: "transparent",
                            "&:hover": { 
                              transform: "translateY(-4px)",
                              boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                              borderColor: "rgba(102, 126, 234, 0.3)",
                            },
                          }}
                          onClick={() => navigate(`/articles/${relArticle.slug}`)}
                        >
                          <CardActionArea>
                            {relArticle.thumbnail_image && (
                              <CardMedia
                                component="img"
                                height="140"
                                image={relArticle.thumbnail_image}
                                alt={relArticle.title}
                                sx={{ objectFit: "cover" }}
                              />
                            )}
                            <CardContent>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 700,
                                  mb: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  lineHeight: 1.4,
                                  color: "#1a1a1a",
                                }}
                              >
                                {relArticle.title}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#999", fontWeight: 500 }}>
                                {formatDate(relArticle.published_at)}
                              </Typography>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      ))}
                    </Stack>
                  </Paper>
                </Fade>
              ) : null}

              {/* Enhanced CTA */}
              <Fade in timeout={1000}>
                <Paper
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 8px 24px rgba(102, 126, 234, 0.3)",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 60%)",
                      pointerEvents: "none",
                    },
                  }}
                >
                  <Box sx={{ position: "relative", zIndex: 1 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        mb: 2,
                        fontSize: "1.375rem",
                      }}
                    >
                      Ready to Get Started?
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mb: 3, 
                        opacity: 0.95,
                        lineHeight: 1.6,
                        fontSize: "0.95rem",
                      }}
                    >
                      Join thousands of brands using Blitz to power their influencer campaigns.
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{ 
                        bgcolor: "white", 
                        color: "#667eea",
                        fontWeight: 700,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "1rem",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        "&:hover": { 
                          bgcolor: "#f5f5f5",
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                        },
                        transition: "all 0.2s",
                      }}
                      onClick={() => navigate("/onboard")}
                    >
                      Get Started Free
                    </Button>
                  </Box>
                </Paper>
              </Fade>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <HomeFooter />
    </Box>
  );
};

export default ArticleDetail;